import naja from 'naja';
import netteForms from 'nette-forms';
import { hideModal, removeModal, showModal } from './modal';
import { configureAdvertiserForms, prepareAdvertiserModalForm } from './forms';
import { advertiserText } from './i18n';
import { registerStripeCheckout } from './stripe-checkout';

configureAdvertiserForms(netteForms);
naja.formsHandler.netteForms = netteForms;
registerStripeCheckout(naja);

globalThis.naja = naja;
globalThis.Nette = netteForms;
globalThis.showModal = showModal;
globalThis.hideModal = hideModal;
globalThis.removeModal = removeModal;

let activeRequests = 0;
let keepLoaderVisible = false;

function setLoaderVisible(visible) {
    document.querySelectorAll('.loader').forEach(loader => loader.classList.toggle('show', visible));
}

function initDynamicContent(root = document) {
    const forms = root instanceof HTMLFormElement ? [root] : root.querySelectorAll('form');
    forms.forEach(prepareAdvertiserModalForm);
}

function scrollToFlash() {
    const flash = document.querySelector('.flash');
    if (!flash) {
        return;
    }

    const navbarHeight = document.querySelector('.navba')?.getBoundingClientRect().height ?? 0;
    const top = flash.getBoundingClientRect().top + window.scrollY - navbarHeight - 15;
    window.scrollTo({top, behavior: 'smooth'});
}

async function loadPayloadUrl(url) {
    try {
        await naja.makeRequest('GET', url, null, {history: false});
    } catch (error) {
        console.error('Advertiser AJAX request failed.', error);
    }
}

naja.uiHandler.addEventListener('interaction', event => {
    event.detail.options.history = false;
});

naja.addEventListener('start', () => {
    activeRequests += 1;
    setLoaderVisible(true);
});

naja.addEventListener('success', event => {
    const payload = event.detail.payload ?? {};

    if (payload.dontHideLoader !== undefined) {
        keepLoaderVisible = true;
    }
    if (payload.showModal !== undefined) {
        window.setTimeout(() => {
            initDynamicContent(document);
            showModal();
        });
    }
    if (payload.ajaxLoad) {
        void loadPayloadUrl(payload.ajaxLoad);
    }
    if (payload.ajaxReload) {
        void loadPayloadUrl(payload.ajaxReload);
    }
});

naja.addEventListener('complete', event => {
    activeRequests = Math.max(0, activeRequests - 1);
    initDynamicContent(document);
    scrollToFlash();

    if (activeRequests === 0 && !keepLoaderVisible && event.detail.payload?.ajaxLoad === undefined) {
        setLoaderVisible(false);
    }
});

naja.snippetHandler.addEventListener('afterUpdate', event => initDynamicContent(event.detail.snippet));

document.addEventListener('click', event => {
    if (event.target.closest('nav.mobile a')) {
        document.querySelectorAll('nav.active, .hamburger.active').forEach(element => element.classList.remove('active'));
        return;
    }

    const hamburger = event.target.closest('.hamburger');
    if (hamburger) {
        hamburger.classList.toggle('active');
        document.querySelector('nav.mobile')?.classList.toggle('active');
        return;
    }

    const hashLink = event.target.closest('.hash-scroll');
    if (hashLink) {
        const target = document.querySelector(hashLink.getAttribute('href'));
        if (target) {
            event.preventDefault();
            const headerHeight = document.querySelector('header')?.getBoundingClientRect().height ?? 0;
            window.scrollTo({top: target.getBoundingClientRect().top + window.scrollY - headerHeight, behavior: 'smooth'});
        }
    }
});

document.addEventListener('click', async event => {
    const button = event.target.closest('[data-company-lookup]');
    if (!button || button.disabled) {
        return;
    }

    const form = button.closest('form');
    const queryInput = form?.querySelector('[data-company-lookup-input]');
    const status = form?.querySelector('[data-company-lookup-status]');
    const resultsElement = form?.querySelector('[data-company-lookup-results]');
    if (!form || !queryInput || !status || !resultsElement) {
        return;
    }

    const query = queryInput.value.trim();
    resultsElement.replaceChildren();
    if (query.length < 3) {
        status.textContent = advertiserText('companyLookupShortQuery');
        return;
    }

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = advertiserText('companyLookupSearching');
    status.textContent = advertiserText('companyLookupSearching');
    try {
        const url = new URL(form.action || window.location.href, window.location.href);
        url.searchParams.set('do', 'getCompany');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: new URLSearchParams({query}),
        });
        const raw = await response.json();
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        if (data.lookupUnavailable === true || !response.ok) {
            status.textContent = typeof data.message === 'string'
                ? data.message
                : advertiserText('companyLookupUnavailable');
            return;
        }

        const results = Array.isArray(data.results) ? data.results : [];
        if (results.length === 0) {
            status.textContent = advertiserText('companyLookupNoResults');
            return;
        }

        status.textContent = '';
        for (const company of results) {
            if (!company || typeof company.name !== 'string') {
                continue;
            }
            const address = [company.street, [company.zip, company.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
            const choice = document.createElement('button');
            choice.type = 'button';
            choice.className = 'btn btn-light border text-start w-100 my-1';
            choice.textContent = address ? `${company.name} · ${address}` : company.name;
            choice.addEventListener('click', () => {
                const fields = {
                    name: company.name,
                    street: company.street,
                    zip: company.zip,
                    city: company.city,
                };
                for (const [name, value] of Object.entries(fields)) {
                    const field = form.elements.namedItem(name);
                    if (field && typeof value === 'string' && value !== '') {
                        field.value = value;
                        field.dispatchEvent(new Event('input', {bubbles: true}));
                        field.dispatchEvent(new Event('change', {bubbles: true}));
                    }
                }
                resultsElement.replaceChildren();
                status.textContent = advertiserText('companyLookupSelected');
            });
            resultsElement.append(choice);
        }
    } catch (error) {
        status.textContent = advertiserText('companyLookupUnavailable');
        console.error('German company lookup failed.', error);
    } finally {
        button.disabled = false;
        button.textContent = originalText;
    }
});

document.addEventListener('focusout', async event => {
    const input = event.target.closest('.input-ico');
    if (!input) {
        return;
    }

    const bottom = input.parentElement.querySelector('.bottom') ?? input.parentElement.appendChild(document.createElement('div'));
    bottom.classList.add('bottom');

    let loader = bottom.querySelector('.loader.ico-loader');
    if (!loader) {
        loader = document.createElement('div');
        loader.className = 'loader ico-loader';
        loader.innerHTML = '<div class="bounce1"></div><div class="bounce2"></div><div class="bounce3"></div>';
        bottom.append(loader);
    }

    let alert = bottom.querySelector('.ico-wrong');
    if (!alert) {
        alert = document.createElement('small');
        alert.className = 'ico-wrong text-red';
        bottom.append(alert);
    }

    let companyName = bottom.querySelector('.ico-success');
    if (!companyName) {
        companyName = document.createElement('small');
        companyName.className = 'ico-success';
        bottom.append(companyName);
    }

    loader.classList.remove('d-none');
    loader.hidden = false;
    alert.textContent = '';
    alert.classList.add('d-none');
    alert.hidden = true;
    companyName.textContent = '';
    companyName.classList.add('d-none');
    companyName.hidden = true;

    try {
        const unavailableMessage = advertiserText('companyLookupUnavailable');
        if (unavailableMessage) {
            alert.textContent = unavailableMessage;
            alert.classList.remove('d-none');
            alert.hidden = false;
            return;
        }

        const url = new URL(input.dataset.url, window.location.href);
        url.searchParams.set('do', 'getCompany');
        const body = new URLSearchParams({ico: input.value});
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body,
        });
        const responseData = await response.json();
        const data = typeof responseData === 'string' ? JSON.parse(responseData) : responseData;
        if (data.lookupUnavailable === true) {
            alert.textContent = typeof data.message === 'string'
                ? data.message
                : advertiserText('companyLookupFailed');
            alert.classList.remove('d-none');
            alert.hidden = false;
            return;
        }
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        if (typeof data.name === 'string' && data.name.length > 2) {
            alert.textContent = '';
            alert.classList.add('d-none');
            alert.hidden = true;
            companyName.textContent = data.name;
            companyName.classList.remove('d-none');
            companyName.hidden = false;
        } else {
            alert.textContent = advertiserText('companyNotFound');
            alert.classList.remove('d-none');
            alert.hidden = false;
        }
    } catch (error) {
        alert.textContent = advertiserText('companyLookupFailed');
        alert.classList.remove('d-none');
        alert.hidden = false;
        console.error('Company lookup failed.', error);
    } finally {
        loader.classList.add('d-none');
        loader.hidden = true;
    }
});

initDynamicContent(document);
naja.initialize();
